import type { DataImportItem } from "../../../../api/frameworksApi";
import { apiFetch, PAG_BASE_URL } from "../../../../api/frameworksApi";
import type { PagApplication } from "../data/pagTypes";
import type { ScopeNode } from "../../itCompliance/types";

/* ── HTTP Request Helpers ───────────────────────────────────────────── */

const get = <T>(path: string) => apiFetch<T>(path, undefined, PAG_BASE_URL);
const post = <T>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: "POST", body: body !== undefined ? JSON.stringify(body) : undefined }, PAG_BASE_URL);
const del = <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }, PAG_BASE_URL);

/* ── Network Graph ──────────────────────────────────────────────────── */

export interface PagNetworkGraphRequest {
  appCodes: string[];
}

export interface PagNetworkGraphResponse {
  data: PagApplication[];
}

export const fetchPagNetworkGraphData = (appCodes: string[]) =>
  post<PagNetworkGraphResponse>("/awsIam/getNetworkGraphData", { appCodes });

/* ── Main Filter & Scope Tree Transformer ───────────────────────────── */

export interface PagFilterEnvironment {
  environment: string;
  accountNames: string[];
  subscriptionIds?: string[];
}

export interface PagFilterApplication {
  appCode: string;
  appName: string;
  environments: PagFilterEnvironment[];
}

export interface PagFilterCsp {
  csp: string;
  applications: PagFilterApplication[];
}

export interface PagFilterDivision {
  divisionName: string;
  cspList: PagFilterCsp[];
}

export interface PagFilterDepartment {
  departmentName: string;
  divisions: PagFilterDivision[];
}

export interface PagFilterAppItem {
  appCode: string;
  appName?: string;
}

export interface PagFilterDivisionItem {
  divisionName: string;
  applications?: PagFilterAppItem[];
  cspList?: PagFilterCsp[];
}

export interface PagFilterDepartmentItem {
  departmentName: string;
  divisions?: PagFilterDivisionItem[];
}

export interface PagFilterOrgItem {
  organizationName: string;
  departments?: PagFilterDepartmentItem[];
}

export interface PagFilterGroupItem {
  groupName: string;
  organizations?: PagFilterOrgItem[];
}

export const fetchPagMainFilter = () => get<unknown>("/Filter/mainFilterApi");

function extractRawApplications(div: any): any[] {
  if (Array.isArray(div.applications) && div.applications.length > 0) {
    return div.applications;
  }
  if (Array.isArray(div.cspList)) {
    return div.cspList.flatMap((c: any) =>
      Array.isArray(c?.applications) ? c.applications : [],
    );
  }
  return [];
}

function mapApplicationChildren(rawApps: any[], divId: string): ScopeNode[] {
  const seenCodes = new Set<string>();
  const appChildren: ScopeNode[] = [];

  for (const app of rawApps) {
    if (!app) continue;
    const code = app.appCode || app.appName || app.code;
    if (!code || seenCodes.has(code)) continue;
    seenCodes.add(code);
    appChildren.push({
      id: `${divId}/app:${code}`,
      name: app.appName || app.appCode || code,
      type: "application",
      appCode: code,
    });
  }

  return appChildren;
}

function mapDivisionNode(div: any, parentDeptId: string, vIdx: number): ScopeNode {
  const vName = div.divisionName || `Division ${vIdx + 1}`;
  const vId = `${parentDeptId}/div:${vName}`;
  const rawApps = extractRawApplications(div);
  const appChildren = mapApplicationChildren(rawApps, vId);

  return { id: vId, name: vName, type: "division", children: appChildren };
}

function mapDepartmentNode(dept: any, parentPrefix: string, dIdx: number): ScopeNode {
  const dName = dept.departmentName || `Department ${dIdx + 1}`;
  const dId = parentPrefix ? `${parentPrefix}/dept:${dName}` : `dept:${dName}`;
  const divs: any[] = Array.isArray(dept.divisions) ? dept.divisions : [];
  const divChildren: ScopeNode[] = divs.map((div, vIdx) =>
    mapDivisionNode(div, dId, vIdx),
  );

  return { id: dId, name: dName, type: "department", children: divChildren };
}

function mapOrgNode(org: any, parentGroupId: string, oIdx: number): ScopeNode {
  const oName = org.organizationName || `Organization ${oIdx + 1}`;
  const oId = `${parentGroupId}/org:${oName}`;
  const depts: any[] = Array.isArray(org.departments) ? org.departments : [];
  const deptChildren: ScopeNode[] = depts.map((dept, dIdx) =>
    mapDepartmentNode(dept, oId, dIdx),
  );

  return { id: oId, name: oName, type: "org", children: deptChildren };
}

function mapGroupNode(group: any, gIdx: number): ScopeNode {
  const gName = group.groupName || `Group ${gIdx + 1}`;
  const gId = `group:${gName}`;
  const orgs: any[] = Array.isArray(group.organizations) ? group.organizations : [];
  const orgChildren: ScopeNode[] = orgs.map((org, oIdx) =>
    mapOrgNode(org, gId, oIdx),
  );

  return { id: gId, name: gName, type: "group", children: orgChildren };
}

export function mapPagFilterToScopeTree(raw: unknown): ScopeNode[] {
  if (!raw) return [];
  const payload =
    typeof raw === "object" && raw !== null && "data" in raw && !Array.isArray(raw)
      ? (raw as { data: unknown }).data
      : raw;

  const list = Array.isArray(payload) ? payload : [payload];
  if (!list.length || !list[0]) return [];

  const isGroupFormat = list.some(
    (item: any) =>
      item &&
      (typeof item.groupName === "string" || Array.isArray(item.organizations)),
  );

  if (isGroupFormat) {
    return list.map((group, gIdx) => mapGroupNode(group, gIdx));
  }

  const isDeptFormat = list.some(
    (item: any) =>
      item &&
      (typeof item.departmentName === "string" || Array.isArray(item.divisions)),
  );

  if (isDeptFormat) {
    const deptChildren: ScopeNode[] = list.map((dept, dIdx) =>
      mapDepartmentNode(dept, "", dIdx),
    );

    return [
      {
        id: "group:default",
        name: "DevOpsLabs",
        type: "group",
        children: [
          {
            id: "org:default",
            name: "DevOpsLabs India",
            type: "org",
            children: deptChildren,
          },
        ],
      },
    ];
  }

  return [];
}

/* ── Drilldown ──────────────────────────────────────────────────────── */

export type PagDrilldownScope = "users" | "groups" | "roles" | "serviceRoles";
export type PagDrilldownStatus = "pending" | "completed";

export const PAG_DRILLDOWN_STATUS: Record<PagDrilldownStatus, string[]> = {
  pending: ["pending"],
  completed: ["continue", "remove"],
};

export interface PagDrilldownRequest {
  accountId: string;
  appCode?: string;
  status?: string[];
  keyword: string;
  sortByColumn: string;
  sortingOrder: string;
}

export interface PagDrilldownResponse<T> {
  data: T[];
  totalElements: number;
}

export interface PagMfaDevice {
  id: number;
  serialNumber: string;
  enabledDate: number;
  updatedOn: number;
  softDelete: boolean;
}

export interface PagAccessKeyDetail {
  id: number;
  accessKeyId: string;
  region: string | null;
  service: string | null;
  lastUsed: number;
  updatedOn: number;
  softDelete: boolean;
}

export interface PagUserDrilldownRow {
  id: number;
  userId: string;
  userName: string;
  accountId: string;
  cliAccess: boolean;
  consoleAccess: boolean;
  passwordLastUsed: number;
  policyName: string;
  policyArn: string;
  policyType: string;
  severity: string;
  status: string;
  mfaDevices: PagMfaDevice[];
  accessKeys: PagAccessKeyDetail[];
}

export interface PagGroupDrilldownRow {
  id: number;
  groupId: string;
  groupName: string;
  groupType: string;
  policyName: string;
  policyArn: string;
  inlinePolicy: string;
  severity: string;
  status: string;
  userName: string;
  userId: string;
  userArn: string;
  passwordLastUsed: string;
  createdDate: string;
}

export interface PagRoleDrilldownRow {
  id: number;
  roleName: string;
  policyName: string;
  policyArn: string;
  policyType: string;
  status: string;
  severity: string;
}

export interface PagServiceRoleDrilldownRow {
  id: number;
  instanceId: string;
  serviceName: string;
  roleName: string;
  policyName: string;
  policySeverity: string;
  status: string;
}

export const fetchPagUserDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagDrilldownResponse<PagUserDrilldownRow>>(`/awsIam/getAwsUserDetailsWithPaginationAndSearch/${page}/${size}`, body);

export const fetchPagGroupDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagDrilldownResponse<PagGroupDrilldownRow>>(`/awsIam/getAwsGroupUserPolicyPendingAndCompletedDetailsWithPaginationAndSearch/${page}/${size}`, body);

export const fetchPagRoleDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagDrilldownResponse<PagRoleDrilldownRow>>(`/awsIam/getAwsRoleDetailsWithPaginationAndSearch/${page}/${size}`, body);

export const fetchPagServiceRoleDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagDrilldownResponse<PagServiceRoleDrilldownRow>>(`/awsIam/getAwsServiceRolesWithPaginationAndSearch/${page}/${size}`, body);

/* ── Policy Categorization ─────────────────────────────────────────── */

export interface PagCategorizedPolicy {
  id: number;
  policyName: string;
  policyArn: string;
  severity: string;
  updatedOn: number;
  softDelete: boolean;
}

export type PagCategorizedPolicyList = Record<string, PagCategorizedPolicy[]>;

export const fetchCategorizedPolicyList = () =>
  get<PagCategorizedPolicyList>("/awsIam/categorizedPolicyList");

export interface PagCategorizationChange {
  severity: string;
  ids: number[];
}

export const savePolicyCategorization = (changes: PagCategorizationChange[]) =>
  post<unknown>("/awsIam/savePolicyCategorization", changes);

/* ── Review Configuration & Users ───────────────────────────────────── */

export interface PagReviewConfigReviewer {
  userId: string;
  displayName: string;
  email: string;
}

export interface PagReviewConfigAccount {
  accountId: string;
  accountName: string;
  reviewCycle: string;
  dueDate: number;
  nextDue: number;
}

export interface PagReviewConfig {
  cmdbId: string;
  reviewCycle: string;
  dueDate: number;
  nextDue: number;
  updatedDate: number;
  reviewers: PagReviewConfigReviewer[];
  accounts: PagReviewConfigAccount[];
}

export const fetchPagReviewConfig = (cmdbId: string) =>
  get<PagReviewConfig>(`/awsIam/getReviewConfigByApplication/${encodeURIComponent(cmdbId)}`);

export interface PagReviewUser {
  userId: string;
  displayName: string;
  email: string;
  active: boolean;
}

export const fetchPagUsersList = () =>
  get<PagReviewUser[]>("/awsIam/usersList");

export interface PagReviewConfigPayload {
  cmdbId: string;
  reviewCycle: string;
  userIds: string[];
}

export const savePagReviewConfig = (payload: PagReviewConfigPayload) =>
  post<unknown>("/awsIam/saveReviewConfig", payload);

/* ── Review Status Actions ─────────────────────────────────────────── */

export type PagReviewAction = "Continue" | "Remove";

export type PagReviewTargetType =
  | "users"
  | "roles"
  | "serviceRoles"
  | "groupUsers"
  | "groupPolicy"
  | "group";

export interface PagReviewStatusChange {
  ids: number[];
  type: PagReviewTargetType;
  status: PagReviewAction;
}

export const changePagReviewStatus = (body: PagReviewStatusChange) =>
  post<unknown>("/awsIam/changeReviewStatus", body);

export const changePagGroupReviewStatus = (body: PagReviewStatusChange[]) =>
  post<unknown>("/awsIam/changeReviewStatusGroup", body);

/* ── Previous Cycle Drilldowns ──────────────────────────────────────── */

export interface PagPreviousCycleDrilldownResponse {
  data: PagRoleDrilldownRow[];
  totalElements: number;
}

export const fetchPagPreviousCycleDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagPreviousCycleDrilldownResponse>(`/awsIam/getAwsPreviousCycleRoleDetailsWithPaginationAndSearch/${page}/${size}`, body);

export const fetchPagPreviousCycleGroupDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagDrilldownResponse<PagGroupDrilldownRow>>(`/awsIam/getPreviousCycleGroupUserPolicyDetailsWithPaginationAndSearch/${page}/${size}`, body);

export interface PagPreviousCycleUserDrilldownResponse {
  data: PagUserDrilldownRow[];
  count?: number;
  totalElements?: number;
}

export const fetchPagPreviousCycleUserDrilldown = (body: PagDrilldownRequest, page: number, size: number) =>
  post<PagPreviousCycleUserDrilldownResponse>(`/awsIam/getAwsPreviousCycleUserDetailsWithPaginationAndSearch/${page}/${size}`, body);

interface PagPreviousCycleServiceRoleRow {
  id: number;
  instanceId: string;
  service: string;
  roleName: string;
  policyName: string;
  severity: string;
  status: string;
}

export const fetchPagPreviousCycleServiceRoleDrilldown = async (
  body: PagDrilldownRequest,
  page: number,
  size: number,
): Promise<PagDrilldownResponse<PagServiceRoleDrilldownRow>> => {
  const res = await post<{
    data: PagPreviousCycleServiceRoleRow[];
    count?: number;
    totalElements?: number;
  }>(`/awsIam/getAwsPreviousCycleServiceRolesWithPaginationAndSearch/${page}/${size}`, body);

  return {
    data: (res?.data ?? []).map((r) => ({
      id: r.id,
      instanceId: r.instanceId,
      serviceName: r.service,
      roleName: r.roleName,
      policyName: r.policyName,
      policySeverity: r.severity,
      status: r.status,
    })),
    totalElements: res?.totalElements ?? res?.count ?? 0,
  };
};

/* ── Cycle Lists ───────────────────────────────────────────────────── */

export interface PagCycleListRow {
  appName: string;
  appCode: string;
  criticality: string | null;
  businessDepartment: string | null;
  businessDivision: string | null;
  accountId: string;
  accountName: string;
  dueDate: number;
  reviewCycle: string;
  reviewers: string[];
  totalStatusCount: number;
  pendingStatusCount: number;
  removeStatusCount: number;
  continueStatusCount: number;
  groupCount: number;
  roleCount: number;
  serviceCount: number;
  userCount: number;
}

export interface PagCycleListRequest {
  appCodes: string[];
  keyword: string;
}

export interface PagCycleListResponse {
  data: PagCycleListRow[];
  totalElements: number;
}

export const fetchPagCurrentCycleList = (body: PagCycleListRequest, page: number, size: number) =>
  post<PagCycleListResponse>(`/awsIam/getAwsDetailsWithPaginationAndSearch/${page}/${size}`, body);

export const fetchPagPreviousCycleList = (body: PagCycleListRequest, page: number, size: number) =>
  post<PagCycleListResponse>(`/awsIam/getAwsPreviousCycleMainDetailsWithPaginationAndSearch/${page}/${size}`, body);

/* ── On-Prem (MS AD) Privileged Groups & Accounts ──────────────────── */

export interface PagAccountItem {
  sourceSystemId: number;
  accountId: number;
  groupId: number | null;
  groupName?: string | null;
  userName?: string;
  username?: string;
  displayName: string | null;
  userDn?: string | null;
  membershipType?: string;
  groupScope?: string;
  enabled?: boolean;
  email?: string | null;
  hostname?: string | null;
  department?: string | null;
  jobTitle?: string | null;
  riskLevel?: string | null;
  classification?: "AVAILABLE" | "PRIVILEGED" | "NON_PRIVILEGED" | string;
}

export interface PagOnPremAccountItem {
  sourceSystemId: number;
  accountId: number;
  username?: string;
  userName?: string;
  displayName?: string | null;
  externalId?: string | null;
  userPrincipalName?: string | null;
  uid?: string | number | null;
  gid?: string | number | null;
  hostname?: string | null;
  email?: string | null;
  userDn?: string | null;
  ouPath?: string | null;
  department?: string | null;
  jobTitle?: string | null;
  enabled?: boolean;
  locked?: boolean;
  passwordNeverExpires?: boolean;
  passwordLastSet?: number | null;
  lastLogon?: number | null;
  riskLevel?: string | null;
  classification?: "AVAILABLE" | "PRIVILEGED" | "NON_PRIVILEGED" | string;
  membershipType?: string;
  groupScope?: string;
  groupId?: number;
  groupName?: string;
}

export interface PagOnPremGroup {
  sourceSystemId: number;
  groupId: number;
  groupName: string;
  description: string | null;
  groupScope?: string;
  groupCategory?: string;
  memberCount?: number;
  accounts?: (PagAccountItem | PagOnPremAccountItem)[];
  riskLevel?: string | null;
  classification?: "AVAILABLE" | "PRIVILEGED" | "NON_PRIVILEGED" | string;
}

export interface PagNonPrivilegedData {
  groups?: PagOnPremGroup[];
  ungroupedAccounts?: (PagAccountItem | PagOnPremAccountItem)[];
}

export interface PagPaginationItem {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export interface PagPaginationMeta {
  nonPrivilegedGroups?: PagPaginationItem;
  nonPrivilegedAccounts?: PagPaginationItem;
  privilegedGroups?: PagPaginationItem;
  privilegedAccounts?: PagPaginationItem;
  availableGroups?: PagPaginationItem;
  availableAccounts?: PagPaginationItem;
  [key: string]: unknown;
}

export interface PagOnPremGroupList {
  // Updated API fields:
  nonPrivilegedGroups?: PagOnPremGroup[];
  nonPrivilegedAccounts?: PagOnPremAccountItem[];
  privilegedGroups?: PagOnPremGroup[];
  privilegedAccounts?: (PagAccountItem | PagOnPremAccountItem)[];
  availableGroups?: PagOnPremGroup[];
  availableAccounts?: PagOnPremAccountItem[];
  pagination?: PagPaginationMeta;

  // Legacy compatibility:
  groups?: PagOnPremGroup[];
  privileged?: PagOnPremGroup[];
  nonPrivileged?: PagNonPrivilegedData | PagOnPremGroup[];
}

export const fetchPagOnPremGroupList = async (
  sourceSystemIds: number[] = [],
  page: number = 0,
  size: number = 100,
): Promise<PagOnPremGroupList> => {
  const paginatedPath = `/Filter/onPrem/groupListApi/${page}/${size}`;
  try {
    return await post<PagOnPremGroupList>(paginatedPath, { sourceSystemIds });
  } catch (err: any) {
    const msg = String(err?.message || "");
    if (msg.includes("404") || msg.includes("Not Found")) {
      return await post<PagOnPremGroupList>("/Filter/onPrem/groupListApi", { sourceSystemIds });
    }
    throw err;
  }
};

export interface PagPrivilegedGroupsResult {
  privilegedGroups: string[];
  unknownGroups: string[];
  accountsRecorded: number;
  accountsRetired: number;
}

export const savePagPrivilegedGroups = (sourceSystemId: number, groupNames: string[]) =>
  post<PagPrivilegedGroupsResult>("/MsAdPrivilegedAccess/privilegedGroups", { sourceSystemId, groupNames });

/* ── Inventory Breakdown & PAM Coverage ────────────────────────────── */

export interface PagInventoryBreakdownGroup {
  groupId: number;
  groupName: string;
  description: string | null;
  accountCount: number;
  percentage: number;
  privileged: boolean;
  sourceSystemId?: number;
  sourceSystemName?: string;
  sourceSystemType?: string;
}

export interface PagInventoryBreakdown {
  sourceSystemId: number;
  sourceSystemName: string;
  sourceSystemType?: string;
  totalAccounts: number;
  privilegedCount: number;
  privilegedPercentage: number;
  groups: {
    content: PagInventoryBreakdownGroup[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
}

export const fetchPagInventoryBreakdown = (
  sourceSystemIds: number[],
  page: number,
  size: number,
  search = "",
  sortBy?: string,
  sortDir?: "asc" | "desc",
) =>
  post<PagInventoryBreakdown>(`/Inventory/inventory-breakdown/${page}/${size}`, {
    sourceSystemIds,
    search,
    ...(sortBy ? { sortBy, sortDir } : {}),
  });

export interface PagPrivilegedAccountBreakdownGroup {
  groupId: number;
  groupName: string;
  description: string | null;
  privilegedAccountCount: number;
  pamCoveredCount: number;
  uncoveredCount: number;
  pamCoveragePercentage: number;
  sourceSystemId?: number;
  sourceSystemName?: string;
  sourceSystemType?: string;
}

export interface PagPrivilegedAccountBreakdown {
  sourceSystemId: number;
  sourceSystemName: string;
  sourceSystemType?: string;
  totalPrivilegedAccounts: number;
  pamCoveredAccounts: number;
  pamCoveragePercentage: number;
  uncoveredAccounts: number;
  groups: {
    content: PagPrivilegedAccountBreakdownGroup[];
    page: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
  };
}

export const fetchPagPrivilegedAccountBreakdown = (
  sourceSystemIds: number[],
  page: number,
  size: number,
  search = "",
  sortBy?: string,
  sortDir?: "asc" | "desc",
) =>
  post<PagPrivilegedAccountBreakdown>(`/Inventory/pam-coverage/${page}/${size}`, {
    sourceSystemIds,
    search,
    ...(sortBy ? { sortBy, sortDir } : {}),
  });

export interface PagPamCoverageSummary {
  sourceSystemIds: number[];
  totalPrivilegedAccounts: number;
  pamCoveredAccounts: number;
  uncoveredAccounts: number;
  pamCoveragePercentage: number;
}

export const fetchPagPamCoverageSummary = (sourceSystemIds: number[], search = "") =>
  post<PagPamCoverageSummary>("/Inventory/pam-coverage-summary", { sourceSystemIds, search });

/* ── CyberArk Platforms Breakdown ─────────────────────────────────── */

export interface PagCyberarkPlatformItem {
  sourceSystemId: number;
  sourceSystemName?: string;
  platformId: string;
  safeName: string;
  usernameCount: number;
}

export interface PagCyberarkPlatformsResponse {
  content: PagCyberarkPlatformItem[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export const fetchPagCyberarkPlatforms = (
  sourceSystemIds: number[],
  page: number,
  size: number,
  search = "",
  sortBy?: string,
  sortDir?: "asc" | "desc",
) =>
  post<PagCyberarkPlatformsResponse>(`/Inventory/cyberark-platforms/${page}/${size}`, {
    sourceSystemIds,
    search: search || undefined,
    ...(sortBy ? { sortBy, sortDir: sortDir ?? "desc" } : {}),
  });

export interface PagCyberarkAccountItem {
  sourceSystemId: number;
  safeName: string;
  id: string;
  name: string;
  address: string;
  userName: string;
  secretType: string;
  automaticManagementEnabled: boolean;
  managementStatus: string;
  lastModified: string;
  manualManagementReason?: string | null;
  lastVerifiedTime?: string | number | null;
  lastVerified?: string | number | null;
  [key: string]: unknown;
}

export interface PagCyberarkAccountsResponse {
  content: PagCyberarkAccountItem[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export const fetchPagCyberarkAccounts = (
  params: {
    sourceSystemId: number;
    platformId: string;
    safeName: string;
    search?: string;
    sortBy?: string;
    sortDir?: "asc" | "desc";
  },
  page: number,
  size: number,
) =>
  post<PagCyberarkAccountsResponse>(`/Inventory/cyberark-platforms/accounts/${page}/${size}`, {
    sourceSystemId: params.sourceSystemId,
    platformId: params.platformId,
    safeName: params.safeName,
    ...(params.search ? { search: params.search } : {}),
    ...(params.sortBy ? { sortBy: params.sortBy, sortDir: params.sortDir ?? "asc" } : {}),
  });

/* ── Risk & Group Charts ───────────────────────────────────────────── */

export interface PagRiskChartSlice {
  riskLevel: string;
  label: string;
  count: number;
  percentage: number;
}

export interface PagRiskChart {
  sourceSystemId: number;
  totalPrivilegedAccounts: number;
  slices: PagRiskChartSlice[];
}

export const fetchPagRiskChart = (sourceSystemIds: number[]) =>
  post<PagRiskChart>("/AccountRisk/risk-chart", { sourceSystemIds });

export interface PagPrivilegedGroupChartGroup {
  groupId: number;
  groupName: string;
  description: string | null;
  accountCount: number;
  percentage: number;
}

export interface PagPrivilegedGroupChart {
  sourceSystemIds: number[];
  totalPrivilegedGroups: number;
  totalPrivilegedAccounts: number;
  groups: PagPrivilegedGroupChartGroup[];
}

export const fetchPagPrivilegedGroupChart = (sourceSystemIds: number[]) =>
  post<PagPrivilegedGroupChart>("/Inventory/privileged-group-chart", { sourceSystemIds });

/* ── Privileged Account Inventory ──────────────────────────────────── */

export interface PagAccountRow {
  adUserId: number;
  accountName: string;
  displayName: string | null;
  groupNames: string[];
  groupName?: string;
  sourceSystemName: string;
  sourceSystemType?: string;
  pamStatus: string | null;
  reviewStatus: string | null;
  riskLevel: string | null;
  lastLogin: number | null;
  enabled: boolean;
  dueDate?: string | number | null;
  targetDate?: string | number | null;
  reviewDate?: string | number | null;
  isOverdue?: boolean;
}

export interface PagAccountListResponse {
  content: PagAccountRow[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  sortBy: string;
  sortDir: string;
}

export interface PagAccountListRequest {
  sourceSystemIds: number[];
  search?: string | null;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  riskLevels?: string[];
  pamStatuses?: string[];
  groupNames?: string[];
  groupName?: string;
  reviewStatuses?: string[];
}

export const fetchPagAccountList = (body: PagAccountListRequest, page: number, size: number) =>
  post<PagAccountListResponse>(`/accounts/list/${page}/${size}`, body);

export interface PagAccountFiltersResponse {
  groupNames: string[];
  pamStatuses: string[];
  riskLevels: string[];
  reviewStatuses: string[];
}

export const fetchPagAccountFilters = (sourceSystemIds: number[]) =>
  post<PagAccountFiltersResponse>("/accounts/filters", { sourceSystemIds });

export interface PagAccountsByGroupRequest {
  sourceSystemIds: number[];
  groupName: string;
  search?: string | null;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export const fetchPagAccountsByGroup = (
  body: PagAccountsByGroupRequest,
  page: number,
  size: number,
) => post<PagAccountListResponse>(`/accounts/by-group/${page}/${size}`, body);

export interface PagAccountReview {
  reviewId: number;
  reviewRef: string | null;
  status: string | null;
  requestedBy: string | null;
  requestComment: string | null;
  actionPlan: string | null;
  dueDate: string | null;
  reviewedAt: string | null;
  comment: string | null;
  commentedBy: string | null;
  commentedOn: string | null;
}

export interface PagAccountDetails {
  adUserId: number;
  accountName: string;
  displayName: string | null;
  sourceSystemName: string;
  reviews: PagAccountReview[] | null;
}

export const fetchPagAccountDetails = (sourceSystemId: number, adUserId: number) =>
  post<PagAccountDetails>("/accounts/details", { sourceSystemId, adUserId });

export interface PagReviewActionRequest {
  sourceSystemId: number;
  accountNames: string[];
  action: "REVIEW" | "REVOKE";
  dueDate: string;
  justification: string;
  reviewedBy: string;
}

export interface PagReviewActionResult {
  adUserId: number;
  accountName: string;
  review: string;
  reviewLabel: string;
}

export interface PagReviewActionResponse {
  message: string;
  reviewRef: string;
  reviewedBy: string;
  justification: string;
  results: PagReviewActionResult[];
}

export const submitPagReviewAction = (body: PagReviewActionRequest) =>
  post<PagReviewActionResponse>("/accounts/review-action", body);

/* ── Access Review Queue ───────────────────────────────────────────── */

export interface PagReviewRequestRow {
  reviewId: number;
  requestId: string;
  adUserId?: number;
  accountName: string;
  displayName?: string | null;
  groupNames?: string[];
  sourceSystemName?: string | null;
  sourceType?: string | null;
  riskLevel?: string | null;
  lastLogin?: number | null;
  pamStatus?: string | null;
  justification: string | null;
  requestedBy: string | null;
  requestedOn?: string | null;
  targetDate?: string | null;
  status: string | null;
  reviewDate: string | null;
}

export interface PagReviewListResponse {
  content: PagReviewRequestRow[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  sortBy: string;
  sortDir: string;
}

export const fetchPagReviewList = (
  body: { sourceSystemIds: number[]; search?: string; sortBy?: string; sortDir?: "asc" | "desc" },
  page: number,
  size: number,
) => post<PagReviewListResponse>(`/accounts/review-list/${page}/${size}`, body);

/* ── Risk Configuration ────────────────────────────────────────────── */

export interface PagRiskAccount {
  adUserId: number;
  samAccountName: string;
  displayName: string | null;
  enabled: boolean;
  riskLevel: string | null;
  justification: string | null;
  assignedBy: string | null;
  assignedOn: number | null;
  privilegedGroups: string[];
  vaulted: boolean;
}

export interface PagRiskAccountsResponse {
  content: PagRiskAccount[];
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
}

export const fetchPagRiskAccounts = (sourceSystemIds: number[], page: number, size: number) =>
  post<PagRiskAccountsResponse>(`/AccountRisk/accounts/${page}/${size}`, { sourceSystemIds });

export interface PagRiskAssignment {
  adUserId: number;
  riskLevel: string | null;
}

export interface PagRiskConfigResponse {
  message: string;
  status: number;
}

export const savePagRiskConfig = (sourceSystemId: number, assignments: PagRiskAssignment[]) =>
  post<PagRiskConfigResponse>("/AccountRisk/risk-config", { sourceSystemId, assignments });

/* ── Group-wise Risk Configuration ─────────────────────────────────── */

export interface PagGroupRiskItem {
  groupId: number;
  sourceSystemId: number;
  sourceSystemName: string;
  groupName: string;
  description: string | null;
  accountCount: number;
  riskLevel: string | null;
  justification: string | null;
  assignedBy: string | null;
  assignedOn: number | null;
}

export interface PagGroupRiskResponse {
  content: PagGroupRiskItem[];
  page?: number;
  pageSize?: number;
  totalElements: number;
  totalPages?: number;
}

export interface PagGroupRiskAssignment {
  groupId: number;
  riskLevel: string | null;
}

export interface PagGroupRiskConfigResponse {
  message: string;
  status: number;
}

export const fetchPagGroupRisk = (sourceSystemIds: number[], page?: number, pageSize?: number) =>
  post<PagGroupRiskResponse>(
    page !== undefined && pageSize !== undefined ? `/AccountRisk/groups/${page}/${pageSize}` : "/AccountRisk/groups",
    { sourceSystemIds },
  );

export const savePagGroupRiskConfig = (sourceSystemId: number, assignments: PagGroupRiskAssignment[]) =>
  post<PagGroupRiskConfigResponse>("/AccountRisk/group-risk-config", { sourceSystemId, assignments });

export interface PagGroupRiskAssignmentItem {
  groupId?: number;
  groupName?: string;
  accountId?: number;
  riskLevel: string;
  justification?: string;
  assignedBy?: string;
}

export type PagGroupPrivilegeRiskAssignment = PagGroupRiskAssignmentItem;

export interface PagGroupPrivilegeRiskConfigRequest {
  sourceSystemId: number;
  groupNames: string[];
  accountIds: number[];
  assignments: PagGroupRiskAssignmentItem[];
  nonPrivilegedGroupNames: string[];
  nonPrivilegedAccountIds: number[];
}

export interface PagGroupPrivilegeRiskConfigResponse {
  message?: string;
  status?: number;
  data?: unknown;
}

export const savePagGroupPrivilegeRiskConfig = (payload: PagGroupPrivilegeRiskConfigRequest) =>
  post<PagGroupPrivilegeRiskConfigResponse>("/AccountRisk/group-privilege-risk-config", payload);

/* ── Reviewer Decision ─────────────────────────────────────────────── */

export interface PagReviewDecisionRequest {
  sourceSystemId: number;
  accountNames: string[];
  action: "APPROVE" | "REJECT";
  comment: string;
  decidedBy: string;
}

export interface PagReviewDecisionResult {
  adUserId: number;
  accountName: string;
  review: string;
  reviewLabel: string;
}

export interface PagReviewDecisionResponse {
  message: string;
  actionPlan: string;
  comment: string | null;
  commentedBy: string | null;
  commentedOn: string | null;
  results: PagReviewDecisionResult[];
}

export const submitPagReviewDecision = (body: PagReviewDecisionRequest) =>
  post<PagReviewDecisionResponse>("/accounts/review-decision", body);

/* ── Source System Filter ──────────────────────────────────────────── */

export interface PagSourceSystemInstance {
  sourceSystemId: number;
  name: string;
}

export interface PagSourceSystemFilter {
  sourceSystem: string;
  displayName: string;
  instances: PagSourceSystemInstance[];
}

export const fetchPagSourceSystemFilter = async (): Promise<PagSourceSystemFilter[]> => {
  const res = await get<any>("/Filter/onPrem/sourceSystemFilterApi");
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.sourceSystems)) return res.sourceSystems;
  return [];
};

/* ── Scheduler Details ─────────────────────────────────────────────── */

export interface PagSchedulerSavePayload {
  id?: number;
  moduleName: string;
  tool: string;
  time: string;
  alertDays: number;
  importOn: string;
  importFrequency: string;
  status: boolean;
  createBy?: string;
  updateBy?: string;
}

export const fetchPagSchedulers = () => get<DataImportItem[]>("/SchedulerDetails");

export const savePagScheduler = (payload: PagSchedulerSavePayload) =>
  post<DataImportItem>("/SchedulerDetails", payload);

export const deletePagScheduler = (id: number) =>
  del<{ message: string; status: number }>(`/SchedulerDetails/${id}`);

export interface SourceSystemsToolNameResponse {
  tools: string[];
}

export const fetchSourceSystemTools = async (): Promise<string[]> => {
  try {
    const res = await get<any>("/SourceSystems/toolName");
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.tools)) return res.tools;
    if (Array.isArray(res?.data?.tools)) return res.data.tools;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  } catch (error) {
    console.warn("Could not fetch source system tools:", error);
    return [];
  }
};
